'use client';

import './provider-search-filters-count.css';

import type { Key, ReactElement } from 'react';
import { Button } from '@florencelabs/button';
import { Checkbox } from '@florencelabs/checkbox';
import { Radio } from '@florencelabs/radio';
import {
  MobileSelect,
  Select,
  SelectItem,
  SelectProps,
} from '@florencelabs/select';
import clsx from 'clsx';
import { kebabCase } from 'lodash';
import { useParams } from 'next/navigation';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useSearch,
  type FilterKey,
} from '@provider-directory/contexts/ProviderSearchContext';
import {
  FilterDrawerFooter,
  SearchFiltersContext,
  useSearchFilters,
} from '@provider-directory/contexts/SearchFiltersContext';
import { cn } from '@provider-directory/functions/cn';
import { useIsDesktop } from '@provider-directory/hooks/useBreakpoints';
import { useIsPrimaryCareSpecialty } from '@provider-directory/hooks/useIsPrimaryCareSpecialty';
import { LocationData } from '@provider-directory/types/locations.contracts';
import { type FilterField } from '@provider-directory/types/search.model';
import { LanguagesFilter } from './languages-filter.component';
import { LocationFilter } from './location-filter.component';
import { ProviderNamesFilter } from './provider-names-filter.component';
import { ProviderSearchFiltersDrawer } from './provider-search-filters-drawer.component';
import { SpecialtyFilter } from './specialty-filter.component';

const DrawerAwareMobileSelect = <T extends object>(
  props: SelectProps<T>,
): ReactElement => {
  const { isOpen: isDrawerOpen, setIsOpen: setDrawerIsOpen } =
    useSearchFilters();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <MobileSelect
      {...props}
      isOpen={isDrawerOpen && isOpen}
      onOpenChange={setIsOpen}
      showBackButton={true}
      onClose={() => {
        setDrawerIsOpen(false);
      }}
    />
  );
};

export interface ProviderSearchFiltersProps {
  showClear?: boolean;
  className?: string;
  isLocationSelected: boolean;
  isDisabled?: boolean;
}

export const ProviderSearchFiltersForm = ({
  showClear = true,
  isLocationSelected,
  className,
  isDisabled,
}: ProviderSearchFiltersProps): ReactElement => {
  const params = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const { search, searchCriteria, filters, specialtyNames, clear } =
    useSearch();
  const isPrimaryCare = useIsPrimaryCareSpecialty();

  const handleSelectionChange =
    (name: FilterKey) =>
    (option: Set<Key> | Key | LocationData | null): void => {
      search({ key: name, option });
    };

  const renderFormField = ({
    field,
    slug,
  }: {
    field: FilterField;
    slug: string;
  }) => {
    const { component, name, label } = field;
    const paramName = kebabCase(name);
    const param = searchCriteria[paramName as keyof typeof searchCriteria];

    /**
     * MFE request to have isPcp box checked when "primary-care" specialty is selected
     * A short term solution until they fix on the data side
     * Search "isPrimaryCareAndPcp" to see affected components
     * See: https://fabric-health.atlassian.net/browse/DIR-1339
     */
    const isPrimaryCareAndPcp = isPrimaryCare(slug) && name === 'isPcp';

    switch (component) {
      case 'radio':
        return (
          <fieldset key={name}>
            <div className={'space-y-1'}>
              {field.options.map((option) => (
                <div key={option.key} className={'mb-2 flex items-center'}>
                  <Radio
                    testId={`search-filter-radio-${name}-${option.key}`}
                    id={`${name}-${option.key}`}
                    key={option.key}
                    name={name}
                    value={option.key}
                    disabled={isDisabled || option.isDisabled}
                    checked={
                      param === option.key || (!!option.isDefault && !param)
                    }
                    onChange={(e) => {
                      handleSelectionChange(name)(e.target.value);
                    }}
                    className={'text-base font-medium'}
                  >
                    <span>{t(option.label)}</span>
                    {option.count !== undefined && (
                      <span
                        className={
                          'provider-search-filter-option-count ml-2 text-neutral-500'
                        }
                      >
                        {option.count}
                      </span>
                    )}
                  </Radio>
                </div>
              ))}
            </div>
          </fieldset>
        );
      case 'select':
        return (
          <div className={'mb-4 flex flex-col'} key={name}>
            {name === 'specialties' ? (
              <SpecialtyFilter
                value={slug}
                id={name}
                isDisabled={isDisabled}
                label={t(field.placeholder)}
                name={name}
                specialtyNames={specialtyNames}
                // eslint-disable-next-line @typescript-eslint/no-deprecated -- current react-aria-components 1.8.0 API; deprecation via deduped react-aria
                onSelectionChange={handleSelectionChange(name)}
              />
            ) : name === 'providerNames' ? (
              <ProviderNamesFilter
                id={name}
                isDisabled={isDisabled}
                label={t(field.placeholder)}
                name={name}
              />
            ) : name === 'geolocation' ? (
              <LocationFilter
                id={'address'}
                isDisabled={isDisabled}
                label={t(field.placeholder)}
                name={'address'}
                onSelectionChange={handleSelectionChange('address')}
              />
            ) : name === 'languages' ||
              name === 'daysOfWeek' ||
              name === 'timeOfDay' ? (
              <LanguagesFilter
                id={name}
                isDisabled={isDisabled}
                items={field.options}
                label={t(field.placeholder)}
                name={name}
                urlParam={
                  name === 'daysOfWeek'
                    ? 'days-of-week'
                    : name === 'timeOfDay'
                      ? 'time-of-day'
                      : 'languages'
                }
                onSelectionChange={handleSelectionChange(name)}
              />
            ) : isDesktop ? (
              <Select
                testIdPrefix={'search-filter'}
                id={name}
                aria-label={
                  field.options.find((option) => param === option.slug)?.key ??
                  field.defaultKey ??
                  t(field.placeholder)
                }
                isDisabled={isDisabled}
                label={t(field.placeholder)}
                name={name}
                isRequired={field.required}
                clearable={!field.required}
                className={'flex w-full'}
                items={field.options}
                onSelectionChange={handleSelectionChange(name)}
                // @ts-expect-error bigint Key
                selectedKey={
                  field.options.find((option) => param === option.slug)?.key ??
                  field.defaultKey ??
                  null
                }
              >
                {field.options.map((option) => (
                  <SelectItem
                    contentClassName={'whitespace-normal'}
                    data-testid={`search-filter-${name}-option-${option.key}`}
                    id={option.key}
                    key={option.key}
                    value={option}
                    textValue={option.key}
                    isDisabled={option.isDisabled}
                  >
                    <span>{t(option.label, { count: +option.key })}</span>
                    {option.count !== undefined && (
                      <span
                        className={
                          'provider-search-filter-option-count ml-2 text-neutral-500'
                        }
                      >
                        {option.count}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </Select>
            ) : (
              <DrawerAwareMobileSelect
                testIdPrefix={'mobile-search-filter'}
                id={name}
                isDisabled={isDisabled}
                label={t(field.placeholder)}
                name={name}
                isRequired={field.required}
                clearable={!field.required}
                className={'w-full'}
                items={field.options}
                aria-label={
                  field.options.find((option) => param === option.slug)?.key ??
                  field.defaultKey ??
                  t(field.placeholder)
                }
                onSelectionChange={handleSelectionChange(name)}
                // @ts-expect-error bigint Key
                selectedKey={
                  field.options.find((option) => param === option.slug)?.key ??
                  field.defaultKey ??
                  null
                }
                popoverFooter={
                  <FilterDrawerFooter
                    isClearDisabled={!param}
                    onClear={
                      !field.required
                        ? () => {
                            search({ key: name, option: null });
                          }
                        : undefined
                    }
                  />
                }
              >
                {field.options.map((option) => {
                  return (
                    <SelectItem
                      className={(state) => {
                        return cn({
                          'text-primary-color': state.isSelected,
                        });
                      }}
                      data-testid={`search-filter-${name}-option-${option.key}`}
                      isMobile={true}
                      id={option.key}
                      key={option.key}
                      value={option}
                      textValue={option.key}
                      isDisabled={option.isDisabled}
                    >
                      <span>{t(option.label, { count: +option.key })}</span>
                      {option.count !== undefined && (
                        <span
                          className={
                            'provider-search-filter-option-count ml-2 text-neutral-500'
                          }
                        >
                          {option.count}
                        </span>
                      )}
                    </SelectItem>
                  );
                })}
              </DrawerAwareMobileSelect>
            )}
          </div>
        );
      case 'checkbox':
        return (
          <div key={name} className={'mb-2 flex items-center'}>
            <Checkbox
              testId={`search-filter-checkbox-${name}`}
              id={name}
              disabled={isDisabled || isPrimaryCareAndPcp}
              checked={param === 'true' || isPrimaryCareAndPcp}
              name={name}
              onChange={(e) => {
                search({ key: name, option: e.target.checked ? 'true' : null });
              }}
            >
              <span
                className={clsx('text-base font-medium', {
                  'cursor-not-allowed text-neutral-500': isPrimaryCareAndPcp,
                  'cursor-pointer': !isPrimaryCareAndPcp,
                })}
              >
                {t(label)}
              </span>
            </Checkbox>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <form
      data-testid={'search-filters-form'}
      className={clsx('rounded-lg bg-white p-6', className)}
    >
      {filters?.map(({ component, label, fields }) => (
        <div key={label} className={'mb-8'}>
          {component === 'group' && label !== null && (
            <div className={'align-center mb-4 flex space-x-2'}>
              <legend
                className={'text-sm font-semibold leading-6 text-gray-900'}
              >
                {t(label)}
              </legend>
            </div>
          )}
          {fields.map((field) => {
            return field.name !== 'maxDistance' || isLocationSelected
              ? renderFormField({ field, slug: params.slug })
              : null;
          })}
        </div>
      ))}
      {showClear ? (
        <Button
          testId={'search-filters-clear-btn'}
          isDisabled={isDisabled}
          onPress={clear}
          type={'button'}
          variant={'systemOutline'}
          className={'mt-10 w-full font-bold'}
        >
          {t('searchFilters.clear')}
        </Button>
      ) : null}
    </form>
  );
};

export const ProviderSearchFilters = ({
  isLoading,
  totalCount,
  ...props
}: ProviderSearchFiltersProps & {
  isLoading: boolean;
  totalCount: number;
}): ReactElement => {
  const { clear } = useSearch();
  const isDesktop = useIsDesktop();
  const [isOpen, setIsOpen] = useState(false);
  const [resultsCountOverride, setResultsCountOverride] = useState<
    number | undefined
  >(undefined);
  const showResultsHandlersRef = useRef<Set<() => void>>(new Set());

  const registerOnShowResults = useCallback((handler: () => void) => {
    showResultsHandlersRef.current.add(handler);
    return () => {
      showResultsHandlersRef.current.delete(handler);
    };
  }, []);

  const notifyShowResults = useCallback(() => {
    showResultsHandlersRef.current.forEach((handler) => {
      handler();
    });
  }, []);

  return (
    <SearchFiltersContext.Provider
      value={{
        isLoading,
        totalCount,
        resultsCountOverride,
        setIsOpen,
        isOpen,
        setResultsCountOverride,
        registerOnShowResults,
        notifyShowResults,
      }}
    >
      {isDesktop ? (
        <div className={'col-span-4 block'}>
          <ProviderSearchFiltersForm {...props} />
        </div>
      ) : (
        <ProviderSearchFiltersDrawer
          isLoading={isLoading}
          totalCount={totalCount}
          onClear={clear}
        >
          <ProviderSearchFiltersForm
            {...props}
            className={'max-h-[calc(100%-9.625rem)] overflow-y-auto'}
            showClear={false}
          />
        </ProviderSearchFiltersDrawer>
      )}
    </SearchFiltersContext.Provider>
  );
};
